import React, { useState, useEffect, useRef } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import HistoryModal from '../components/HistoryModal';
import { progressService } from '../services/progressService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';

interface Step7Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step7: React.FC<Step7Props> = ({ user, onLogin, onLogout }) => {
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Seven",
      description: "Explore how humble prayer can unlock removal of my shortcomings and invite spiritual freedom",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "How to work Step Seven (guidance & video)",
      description: "Read guidance and watch video on how to work through Step Seven",
      completed: false,
      buttons: [
        {
          text: "Read Guidance",
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: "Watch Video",
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: "Take Step Seven: Pray the Seventh Step Prayer (my own or the AA version)",
      description: "I am uplifted by praying this prayer often",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const [prayerText, setPrayerText] = useState<string>('');
  const [savingPrayer, setSavingPrayer] = useState(false);
  const [savedPrayer, setSavedPrayer] = useState(false);
  const [savePrayerError, setSavePrayerError] = useState(false);
  const prayerSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prayerTextRef = useRef<string>('');
  const actionsRef = useRef<ActionItem[]>([]);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Seven', path: '/saa/12-steps/step-7', active: true }
  ];

  // Initialize progress tracking
  const initializeProgress = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    
    try {
      // Initialize SAA progress
      await progressService.initializeProgress();
      
      // Fetch progress data
      const existingProgress = await progressService.getStepProgress('SAA', '12-steps', 7).catch(() => null);
      
      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);
        
        // Restore actions state
        if (savedData.actions) {
          // Merge saved actions with original actions to restore missing properties
          const mergedActions = originalActions.map(originalAction => {
            const savedAction = savedData.actions.find((a: any) => a.id === originalAction.id);
            if (savedAction) {
              return {
                ...originalAction,
                completed: savedAction.completed,
                // Always use original description - it's app UI, not user data
                description: originalAction.description
              };
            }
            return originalAction;
          });
          setActions(mergedActions);
        }
        
        // Restore prayer text
        if (savedData.prayer_text) {
          setPrayerText(savedData.prayer_text);
        }
      }
      
      // Check date tracking preference
      try {
        const journeyData = await progressService.getJourneyProgress();
        setIsOptedOut(journeyData.opted_out);
      } catch (error) {
        console.error('Error loading date tracking preference:', error);
      }
    } catch (err: any) {
      console.error('Error initializing progress:', err);
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    initializeProgress();
  }, [user]);

  // Keep refs in sync for auto-save (avoids stale closure overwriting other data)
  prayerTextRef.current = prayerText;
  actionsRef.current = actions;

  // Perform the actual prayer save - uses refs to get latest actions
  const savePrayerToApi = async (textToSave: string) => {
    if (!user) return;
    setSavingPrayer(true);
    setSavePrayerError(false);
    setSavedPrayer(false);
    try {
      const currentActions = actionsRef.current;
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 7,
        step_title: 'Step Seven',
        completed: currentActions.every(a => a.completed),
        data: {
          actions: currentActions,
          prayer_text: textToSave
        }
      });
      setSavingPrayer(false);
      setSavedPrayer(true);
      setTimeout(() => setSavedPrayer(false), 2000);
    } catch (err: any) {
      console.error('Error auto-saving prayer:', err);
      setSavingPrayer(false);
      setSavePrayerError(true);
      setTimeout(() => setSavePrayerError(false), 3000);
      handleError(err, 'Failed to save prayer. Please try again.');
    }
  };

  // Auto-save prayer text with debouncing
  const autoSavePrayer = (newPrayerText?: string) => {
    if (prayerSaveTimeoutRef.current) {
      clearTimeout(prayerSaveTimeoutRef.current);
    }
    prayerSaveTimeoutRef.current = setTimeout(() => {
      const textToSave = newPrayerText !== undefined ? newPrayerText : prayerTextRef.current;
      savePrayerToApi(textToSave);
    }, 300);
  };

  // Cleanup prayer save timeout on unmount
  useEffect(() => {
    return () => {
      if (prayerSaveTimeoutRef.current) {
        clearTimeout(prayerSaveTimeoutRef.current);
      }
    };
  }, []);

  // Save prayer when user switches tabs
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (prayerSaveTimeoutRef.current) {
          clearTimeout(prayerSaveTimeoutRef.current);
          prayerSaveTimeoutRef.current = null;
        }
        const textToSave = prayerTextRef.current;
        if (textToSave && user) {
          savePrayerToApi(textToSave);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    // For other actions, save to backend
    if (!user) {
      handleError(new Error('User not logged in'), 'Please log in to save your progress.');
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 7,
        step_title: 'Step Seven',
        completed: allCompleted,
        data: {
          actions: updatedActions,
          prayer_text: prayerText
        }
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Seven - ${completedCount} of 3 actions completed`,
        progress_milestone: allCompleted ? 'step7_completed' : 'step7_in_progress'
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Seven</h1>
            {stepData && (
              <HistoryButton
                stepNumber={7}
                stepTitle="Step Seven"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            Humbly asked Him to remove our shortcomings.
          </p>
        </div>

        {isInitializing && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!user && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {!isInitializing && !errorMessage && user && (
          <div className={styles.actionsContainer}>
            <div className={styles.actionsHeader}>
              <h2 className={styles.actionsTitle}>Actions to Complete Step Seven</h2>
            </div>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you've finished each action.
            </p>
            
            <StepActions
              actions={actions}
              onActionChange={handleActionChange}
              stepNumber={7}
              renderActionContent={(action, index) => {
                if (action.id === 3) {
                  return (
                    <>
                      <div style={{ marginBottom: 16, width: '100%' }}>
                        <div style={{ 
                          backgroundColor: '#f8f9fa', 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 8, 
                          padding: 20, 
                          marginBottom: 20,
                          fontStyle: 'italic',
                          lineHeight: 1.6,
                          color: '#333'
                        }}>
                          <h4 style={{ 
                            margin: '0 0 12px 0', 
                            color: '#333', 
                            fontStyle: 'normal',
                            fontWeight: 600
                          }}>
                            7th Step Prayer (from Alcoholics Anonymous, page 76)
                          </h4>
                          <p style={{ margin: '0 0 4px 0', paddingLeft: '20px' }}>
                            My Creator, I am now willing that you should have all of me, good and bad. I pray that you now remove from me every single defect of character which stands in the way of my usefulness to you and my fellows. Grant me strength as I go out from here to do your bidding. Amen
                          </p>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ 
                            display: 'block', 
                            fontWeight: 500, 
                            color: '#333', 
                            marginBottom: 8 
                          }}>
                            Seventh Step Prayer in my own words
                          </label>
                          <textarea
                            value={prayerText}
                            onChange={e => {
                              const val = e.target.value;
                              setPrayerText(val);
                              prayerTextRef.current = val;
                              autoSavePrayer(val);
                            }}
                            onBlur={() => autoSavePrayer()}
                            rows={8}
                            style={{ 
                              width: '100%', 
                              borderRadius: 4, 
                              border: '1px solid #ccc', 
                              padding: 8, 
                              boxSizing: 'border-box',
                              resize: 'vertical'
                            }}
                            placeholder="Write your own version of the Seventh Step Prayer here..."
                          />
                          <div style={{ 
                            textAlign: 'right', 
                            marginTop: 8, 
                            minHeight: 24,
                            fontSize: '14px',
                            fontWeight: 500
                          }}>
                            {savingPrayer && (
                              <span style={{ color: '#6c757d' }}>Saving...</span>
                            )}
                            {savedPrayer && (
                              <span style={{ color: '#28a745' }}>✓ Saved</span>
                            )}
                            {savePrayerError && (
                              <span style={{ color: '#dc3545' }}>⚠ Save failed. Please try again.</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  );
                }
                return null;
              }}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>🎉 Congratulations!</h3>
                  <p>You have completed all actions for Step Seven and can move on to Step Eight.</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-6" className={styles.prevStepLink}>
                      {'<<'} Step Six
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-8" className={styles.nextStepLink}>
                      Step Eight {'>>'}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-6" className={styles.prevStepLink}>
                    {'<<'} Step Six
                  </a>
                  <span></span>
                  <a href="/saa/12-steps/step-8" className={styles.nextStepLink}>
                    Step Eight {'>>'}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-seven start>>"
        endTag="<<step-seven end>>"
      />

      {/* ContentReader for How to Work Step Seven */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_7_Guidance.md"
        startTag="<<working-step-seven start>>"
        endTag="<<working-step-seven end>>"
        title="Guidance on working Step Seven"
      />

      {/* Video Modal for Step Seven */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="eGgHCYElqDw"
        title="Step Seven Video Guide"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={7}
        stepTitle="Step Seven"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          // Refresh step data
          if (user) {
            initializeProgress();
          }
        }}
      />

    </div>
  );
};

export default Step7; 
